import _ from 'lodash'
import React, { useContext, useState } from 'react'
import styled from 'styled-components'
import { VictoryAxis, VictoryChart, VictoryLine, VictoryScatter } from 'victory'
import { chartTheme } from '../../../chartTheme'
import CardTemplate from '../../../components/CardTemplate'
import { Flex } from '../../../components/Flex'
import { TitleWithSub } from '../../../components/TitleWithSub'
import {
  trendColor,
  TrendPercentage,
} from '../../../components/TrendPercentage'
import { Body1, Body1Bold, H4Bold, H6 } from '../../../components/Typography'
import {
  Category,
  FilterInput,
  RefernceValueFilter,
} from '../../../graphql/generated'
import { useElementSize } from '../../../hooks/useElementSize'
import { useFilterInput } from '../../../hooks/useFilterInput'
import {
  getNumberFormatter,
  unitDescription,
} from '../../../utils/adaptiveNumberFormat'
import {
  calculateDomain,
  DataPoint,
  lineChartDotSize,
  lineChartLineWidth,
} from '../../../utils/chart'
import { roundToDecimals } from '../../../utils/math'
import { testHasData } from '../../../utils/testHasData'
import {
  LineChartData,
  LineChartInfo,
  useCategoryLineCharts,
} from './useCategoryLineCharts'
import { colors, spacing } from '../../../theme'
import { FilterContext } from '../../../context/FilterContext'

const Card = styled(Flex)<{
  width?: number | string
  height?: number | string
  // eslint-disable-next-line prettier/prettier
}> `
  background-color: ${colors.white};
  color: ${colors.black};
  width: ${(props) => props.width && `${props.width}`};
  height: ${(props) => props.height && `${props.height}`};
  padding: 0;
`

const PercentageText = styled(H4Bold)`
  font-size: smaller;
  line-height: unset;
`

const ValueText = styled(H6)`
  font-size: smaller;
  line-height: unset;
`

export function CategoryLineChartCard(): React.JSX.Element {
  const [referenceFilter, setReferenceFilter] = useState(
    RefernceValueFilter.None,
  )
  const [category, setCategory] = useState<Category | undefined>()
  const filter = useFilterInput(category, undefined, referenceFilter)

  const categoryLinesData = useCategoryLineCharts(filter)

  return (
    <CardTemplate
      titleData={{
        title: 'Trendutveckling per kategori',
        subtitle: `${unitDescription(filter)} / år`,
      }}
      loading={categoryLinesData.loading}
      error={categoryLinesData.error}
      hasData={testHasData(categoryLinesData.categoryTrends)}
      referenceFilter={referenceFilter}
      setReferenceFilter={setReferenceFilter}
      tooltip={
        <Body1>
          Visar utsläpp och utvecklingstrenden över tid, per kategori.
          Procentvärdet representerar den genomsnittliga årliga utvecklingen
          över alla avslutade år med tillgänglig data.
        </Body1>
      }
      selectedCategory={category}
      setCategory={setCategory}
    >
      <div style={{ padding: spacing.large }}>
        <CategoryLineCharts catLineChart={categoryLinesData} filter={filter} />
      </div>
    </CardTemplate>
  )
}

type Props = {
  catLineChart: LineChartInfo
  filter: FilterInput
}
export function CategoryLineCharts({
  catLineChart,
  filter,
}: Props): React.JSX.Element {
  const { categoryTrends } = catLineChart

  const [ref, size] = useElementSize()

  const width = 320
  const height = width / 2
  const cols = Math.floor((size?.width ?? 800) / width)

  return (
    <div ref={ref}>
      <Flex doWrap stretchWidth>
        {categoryTrends.map((c) => (
          <SubCard
            key={`line-chart-${c.categoryId}`}
            categoryData={c}
            width={width}
            height={height}
            cols={cols}
            filter={filter}
          />
        ))}
      </Flex>
    </div>
  )
}

type SubCardProps = {
  categoryData: LineChartData
  width: number
  height: number
  cols: number
  filter: FilterInput
}
function SubCard({ categoryData, width, height, cols, filter }: SubCardProps) {
  const {
    categoryName,
    categoryColor,
    percentage,
    emissionPoints: chartData,
    trendPoints: trendData,
    trendRate,
  } = categoryData

  const maxlen = 25
  const shorten = categoryName.length > maxlen
  const displayName = shorten
    ? `${categoryName.substring(0, maxlen - 3)}...`
    : categoryName

  const formatter = getNumberFormatter(
    chartData.map((p) => p.y ?? 0), // gaps are treated as zero, no effect on formatter
    {
      filter,
    },
  )

  const title = (
    <Flex row>
      <Body1Bold>{displayName}</Body1Bold>
    </Flex>
  )

  return (
    <Card
      width={`${100 / cols}%`}
      style={{ padding: '0 20px' }}
      key={`line-chart:${categoryName}`}
      column
    >
      <Flex
        row
        spaceBetween
        itemsCenter
        stretchWidth
        style={{ padding: '30px 5px 5px 20px' }}
      >
        <TitleWithSub
          title={title}
          sub=''
          titleStyle={{ fontSize: 'smaller' }}
          hoverText={shorten ? categoryName : undefined}
        />
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyItems: 'center',
            justifyContent: 'space-around',
          }}
        >
          <TrendPercentage
            percentage={roundToDecimals(percentage, 1)}
            iconSize={25}
            TextComponent={PercentageText}
          />
          {trendRate !== undefined && (
            <ValueText>
              {`(${formatter.format(Math.abs(trendRate))})`}
            </ValueText>
          )}
        </div>
      </Flex>
      <Subchart
        percentage={percentage}
        width={width}
        height={height}
        chartData={chartData}
        categoryColor={categoryColor}
        trendData={trendData}
        trendRate={trendRate}
        categoryName={categoryName}
      />
    </Card>
  )
}

type SubchartProps = {
  percentage: LineChartData['percentage']
  width: number
  height: number
  chartData: LineChartData['emissionPoints']
  categoryColor: LineChartData['categoryColor']
  trendData?: LineChartData['trendPoints']
  trendRate?: LineChartData['trendRate']
  categoryName: LineChartData['categoryName']
}
function Subchart({
  percentage,
  width,
  height,
  chartData,
  categoryColor,
  trendData,
  trendRate,
  categoryName,
}: SubchartProps) {
  const isData = <T,>(x: T | undefined): x is T => x !== undefined
  const hasTrendData = [trendData, trendRate].every(isData)
  let minMax = [
    _.minBy(chartData, (d) => d.x)?.x,
    _.maxBy(chartData, (d) => d.x)?.x,
  ]

  const { yearPeriods } = useContext(FilterContext)
  let tickValues
  // Gap points must be included for the X-axis domain to be computed properly.
  // To keep the Y-axis unaffected by gap points, use an existing y-value.
  const someY = chartData.find((p) => p.y !== null)?.y ?? 0
  let domain = calculateDomain(
    chartData.map<DataPoint>((p) =>
      p.y !== null ? (p as DataPoint) : { ...p, y: someY },
    ),
  )

  // if there is no data set the tick valuses on the domain based on the organisation year periods to display an empty chart
  if (!chartData || chartData.length === 0) {
    tickValues = yearPeriods
      .filter((period) => !period.partialYear)
      .map((period) => period.name)
      .reverse()
    domain = { x: [0, yearPeriods.length], y: [0, 10000] }
    minMax = [_.min(tickValues), _.max(tickValues)]
  }

  return (
    <VictoryChart
      padding={{
        top: 10,
        left: 20,
        right: 20,
        bottom: 30,
      }}
      theme={chartTheme({ width, height })}
      domain={domain}
    >
      <VictoryLine
        style={{
          data: { stroke: categoryColor ?? colors.black, strokeWidth: 4 },
        }}
        data={chartData}
        minDomain={{
          x: 2015,
        }}
        interpolation='linear'
      />
      {/* Require data to be loaded to display the trend line. */}
      {hasTrendData && (
        <VictoryLine
          style={{
            data: {
              stroke: trendColor(percentage).color,
              strokeWidth: 0.66,
              strokeDasharray: '6, 10',
            },
          }}
          data={trendData}
          interpolation='linear'
          name='trend'
        />
      )}
      {
        /* show a buble if the graph has data only for ome year */
        chartData.length === 1 && (
          <VictoryScatter
            data-testid={`dot-chart-${categoryName}`}
            // name={partialYearDotName}
            data={[chartData[0]]}
            size={lineChartDotSize}
            style={{
              data: {
                fill: colors.white,
                stroke: categoryColor ?? colors.black,
                strokeWidth: lineChartLineWidth,
              },
              labels: {
                padding: 20,
              },
            }}
          />
        )
      }
      <VictoryAxis
        dependentAxis
        fixLabelOverlap
        orientation='left'
        tickFormat={() => ''}
      />
      <VictoryAxis
        orientation='bottom'
        style={{
          tickLabels: {
            // Note: Victory types are unclear; it seems the datum type here contains 'text'.
            display: (d: { text?: string }) =>
              minMax.includes(d.text) ? 'flex' : 'none',
          },
        }}
        tickValues={tickValues}
      />
    </VictoryChart>
  )
}
